import React, { useState } from "react";
import { ExternalLink, Eye, Play } from "lucide-react";
import { parseEmbed } from "../lib/embed";

const PlatformIcon = ({ platform, size = 12 }) => {
  if (platform === "tiktok") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
      </svg>
    );
  }
  if (platform === "instagram") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="2" width="20" height="20" rx="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    );
  }
  if (platform === "youtube") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1C4.5 20.5 12 20.5 12 20.5s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1C24 15.9 24 12 24 12s0-3.9-.5-5.8zM9.6 15.5v-7l6.3 3.5z" />
      </svg>
    );
  }
  return null;
};

/**
 * Reusable social-media embed card.
 * Displays an iframe preview of the post (TikTok / Instagram / YouTube).
 * Falls back to a gradient card with brand logo when no URL is parseable.
 *
 * Props:
 *   url          – external URL
 *   title        – card title
 *   subtitle     – small label on top
 *   metric       – e.g. "3.0M Views" (optional)
 *   gradient     – CSS background gradient (fallback + frame accent)
 *   brandLogo    – { src } (optional overlay when fallback)
 *   theme        – "light" (white card) | "dark" (dark card) – default light
 */
const SocialEmbed = ({
  url,
  title,
  subtitle,
  metric,
  gradient,
  brandLogo,
  theme = "light",
}) => {
  const meta = parseEmbed(url);
  const [loaded, setLoaded] = useState(false);

  const isDark = theme === "dark";
  const cardBg = isDark ? "bg-neutral-950" : "bg-white";
  const titleColor = isDark ? "text-white" : "text-neutral-950";
  const subColor = isDark ? "text-neutral-400" : "text-neutral-500";
  const ringColor = isDark ? "ring-white/10" : "ring-black/5";

  // Fallback – gradient card w/ Play button
  if (!meta) {
    return (
      <a
        href={url || "#"}
        target={url ? "_blank" : undefined}
        rel="noreferrer"
        className={`group relative aspect-[9/14] rounded-3xl overflow-hidden block ring-1 ${ringColor} transition-all duration-500 hover:-translate-y-1`}
        style={{ background: gradient || "#0a0a0a" }}
      >
        <div className="absolute inset-0 opacity-40 mix-blend-overlay bg-[radial-gradient(circle_at_30%_25%,rgba(255,255,255,0.4),transparent_50%),radial-gradient(circle_at_70%_75%,rgba(0,0,0,0.3),transparent_55%)]" />
        {brandLogo?.src && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[60%] flex items-center justify-center w-[68%] h-[32%]">
            <img
              src={brandLogo.src}
              alt=""
              className="max-w-full max-h-full object-contain drop-shadow-[0_8px_30px_rgba(0,0,0,0.4)]"
              loading="lazy"
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-[0_0_30px_rgba(255,255,255,0.3)]">
          <Play size={22} className="text-neutral-950 fill-neutral-950 ml-1" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
          {metric && (
            <div className="flex items-center gap-2 text-white/80 text-xs">
              <Eye size={14} />
              <span className="font-semibold">{metric}</span>
            </div>
          )}
          <h3 className="mt-1 font-display font-extrabold text-lg md:text-xl tracking-tight leading-tight">
            {title}
          </h3>
        </div>
      </a>
    );
  }

  const aspectClass =
    meta.aspect === "16/9" ? "aspect-video" : "aspect-[9/16]";

  return (
    <div
      className={`group relative rounded-3xl overflow-hidden ${cardBg} ring-1 ${ringColor} shadow-[0_20px_60px_-30px_rgba(0,0,0,0.3)] hover:-translate-y-1 transition-transform duration-500 flex flex-col`}
      data-testid={`social-embed-${meta.platform}`}
    >
      {/* Header: platform + metric */}
      <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-black/5">
        <div
          className="inline-flex items-center gap-2 text-xs font-bold"
          style={{ color: meta.platform === "tiktok" ? "#fe2c55" : meta.platform === "instagram" ? "#e1306c" : "#ff0033" }}
        >
          <PlatformIcon platform={meta.platform} size={14} />
          <span className="uppercase tracking-[0.1em]">{meta.label}</span>
        </div>
        {metric && (
          <div className={`inline-flex items-center gap-1.5 text-[11px] font-bold ${subColor}`}>
            <Eye size={12} />
            {metric}
          </div>
        )}
      </div>

      {/* Embed iframe */}
      <div
        className={`relative w-full ${aspectClass}`}
        style={{
          background:
            gradient ||
            "linear-gradient(135deg, rgba(236,72,153,0.1), rgba(249,115,22,0.1), rgba(168,85,247,0.1))",
        }}
      >
        {/* Skeleton before iframe load */}
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center animate-pulse">
              <Play size={18} className="text-neutral-950 fill-neutral-950 ml-0.5" />
            </div>
          </div>
        )}
        <iframe
          src={meta.embedUrl}
          title={title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          onLoad={() => setLoaded(true)}
          className="absolute inset-0 w-full h-full"
          style={{ border: 0 }}
        />
      </div>

      {/* Footer: title + CTA */}
      <div className="px-4 md:px-5 py-4 flex items-center justify-between gap-4">
        <div className="min-w-0">
          {subtitle && (
            <div className={`text-[10px] uppercase tracking-[0.2em] font-bold ${subColor}`}>
              {subtitle}
            </div>
          )}
          <div className={`font-display font-extrabold text-sm md:text-base tracking-tight truncate ${titleColor}`}>
            {title}
          </div>
        </div>
        <a
          href={meta.pageUrl}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 inline-flex items-center gap-1 text-xs font-bold"
          style={{ color: meta.platform === "tiktok" ? "#fe2c55" : meta.platform === "instagram" ? "#e1306c" : "#ff0033" }}
          data-testid="social-embed-open-original"
        >
          Auf {meta.label} ansehen
          <ExternalLink size={12} />
        </a>
      </div>
    </div>
  );
};

export default SocialEmbed;
